import React, { useCallback, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LogoBadge, SearchField } from 'shared/base';
import { TagsDisplay } from 'shared/base/components/display/tagsDisplay';
import YAML from 'yaml';

import { Report } from '@/components/reports/types/report';

import { templates } from '../../templates';
import { filterReports } from '../../utils';

export default function TemplateListing({
  handleSelectTemplate,
}: {
  handleSelectTemplate: (templateId?: string) => void;
}) {
  const { palette } = useTheme();
  const [search, setSearch] = useState('');
  const reportTemplates: Report[] = templates.map((template) => ({
    ...YAML.parse(template.yaml).report,
    id: template.id,
  }));

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearch(value);
    },
    [setSearch],
  );

  const filteredTemplates = useMemo(
    () => filterReports(reportTemplates ?? [], search),
    [reportTemplates, search],
  ) as Report[];

  if (!reportTemplates) {
    return null;
  }

  return (
    <Grid container={true} justifyContent={{ md: 'flex-start', xs: 'center' }} spacing={2}>
      <SearchField
        aria-label="Search"
        autoFocus={true}
        fullWidth={true}
        onClear={() => setSearch('')}
        onInput={handleSearchChange}
        placeholder="Search by report name or category"
        size="small"
        value={search}
      />
      {filteredTemplates?.map((template: any) => {
        const { id, logo, shortDescription, tags, title } = template;
        const logoSrc = `/images/report_icons/${logo}.webp`;

        return (
          <Grid key={title} component="span" size={{ lg: 4, md: 6, xs: 12 }}>
            <Card
              aria-label={`Use ${title} template`}
              onClick={() => handleSelectTemplate(id)}
              role="button"
              sx={{
                '&:hover': {
                  backgroundColor: palette.secondary.hovered,
                },
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                height: 186,
                justifyContent: 'space-between',
                width: '100%',
              }}
              tabIndex={0}
            >
              <CardContent>
                <Stack direction="column" gap={2}>
                  <Stack alignItems="center" direction="row" gap={1}>
                    <Box flexShrink={0}>
                      <LogoBadge
                        backgroundColor="transparent"
                        height={40}
                        imgHeight={40}
                        imgWidth={40}
                        src={logoSrc}
                        width={40}
                      />
                    </Box>
                    <TagsDisplay limit={3} showKeyValue={false} tags={tags} />
                  </Stack>
                  <Stack direction="column" gap={0.5}>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      variant="h6"
                    >
                      {title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      variant="body2"
                    >
                      {shortDescription}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
